import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import CTABanner from "@/components/CTABanner";
import { pageMeta, blogCategories as defaultCategories } from "@/data/mockData";
import { useBlogPosts, useBlogCategories } from "@/lib/content";

export default function Blog() {
  const [cat, setCat] = useState("All");
  const { data: blogPosts } = useBlogPosts();
  const { data: blogCategoryDocs, loading: categoriesLoading } = useBlogCategories();

  let blogCategories = categoriesLoading && blogCategoryDocs.length === 0
    ? defaultCategories
    : blogCategoryDocs.map((c) => c.name);

  blogCategories = [...blogCategories].sort((a, b) => {
    const idxA = defaultCategories.indexOf(a);
    const idxB = defaultCategories.indexOf(b);
    if (idxA !== -1 && idxB !== -1) return idxA - idxB;
    if (idxA !== -1) return -1;
    if (idxB !== -1) return 1;
    return a.localeCompare(b);
  });

  const filtered = cat === "All" ? blogPosts : blogPosts.filter((p) => p.category === cat);

  return (
    <>
      <SEO {...pageMeta.blog} />

      <section className="bg-white pt-20 pb-12 md:py-24" data-testid="blog-hero">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#0a2540]/60 font-semibold mb-4">
              Blog & Insights
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#0a2540] leading-[1.05]">
              Property thinking, <span className="italic">made clear.</span>
            </h1>
            <p className="mt-7 text-base text-slate-600 leading-relaxed max-w-2xl mx-auto">
              Frameworks, market views, and timing guides for Singapore homeowners, upgraders, investors, and landlords.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#fafafa] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Categories */}
          <div className="flex flex-wrap justify-center gap-2 mb-12" data-testid="blog-filters">
            {["All", ...blogCategories].map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                data-testid={`blog-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`px-5 py-2.5 text-xs font-medium uppercase tracking-wider rounded-full transition-all ${
                  cat === c
                    ? "bg-[#0a2540] text-white"
                    : "bg-white text-slate-600 border border-gray-200 hover:border-[#0a2540]"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filtered.map((p, i) => (
              <motion.article
                key={p.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.45, delay: i * 0.05 }}
                className="group cursor-pointer"
                data-testid={`blog-card-${p.id}`}
              >
                <div className="relative overflow-hidden bg-gray-100 rounded-2xl mb-5">
                  <img
                    src={p.image}
                    alt={p.title}
                    loading="lazy"
                    className="w-full h-auto group-hover:scale-105 transition-transform duration-700"
                  />
                  <span className="absolute top-4 left-4 bg-white/95 text-[#0a2540] px-3 py-1 text-[10px] font-semibold uppercase tracking-widest rounded-full">
                    {p.category}
                  </span>
                </div>
                <div className="text-xs text-slate-500 mb-2 font-medium">{p.date}</div>
                <h3 className="font-serif text-xl text-[#0a2540] mb-3 leading-snug group-hover:text-[#023e8a] transition-colors">
                  {p.title}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed mb-4 line-clamp-2">
                  {p.excerpt}
                </p>
                <Link
                  to={`/blog/${p.slug}`}
                  data-testid={`blog-read-${p.id}`}
                  className="inline-flex items-center gap-2 text-sm font-medium text-[#0a2540] link-underline"
                >
                  Read More
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <CTABanner />
    </>
  );
}
